import { posthog } from '@/lib/analytics/posthog';

declare global {
    interface Window {
        dataLayer: Array<unknown>;
    }
}

export function trackPageView(url: string): void {
    if (typeof window === 'undefined') return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
        event: 'page_view',
        page_location: url,
    });
}

/**
 * A donor was genuinely SHOWN the ask — a surface naming real amounts, at
 * least half on screen. Never fired for a bare "Support" button, which names
 * no figure and therefore answers no question about giving.
 */
export function trackDonateAskViewed(properties: {
    amountsShown: true;
    amounts: number[];
    currency: string;
    surface: string;
}): void {
    if (typeof window === 'undefined') return;
    try {
        posthog.capture('donate_ask_viewed', properties);
    } catch {
        /* posthog not initialised — nothing to record */
    }
}

/**
 * A visitor clicked a creator-acquisition CTA on a church/ministry directory page
 * (e.g. "Claim it yourself", "Start my support page"). $autocapture's $el_text
 * matching is too fragile to trust for this funnel step (DF-240 investigation,
 * 2026-09-30: 342 /churches pageviews in 30d, zero inferable clicks) — this fires
 * an explicit, named event instead so click-through can be measured directly.
 */
export function trackChurchCreatorCtaClick(properties: {
    cta: string;
    ministryId?: string;
}): void {
    if (typeof window === 'undefined') return;
    try {
        posthog.capture('church_creator_cta_click', properties);
    } catch {
        /* posthog not initialised — nothing to record */
    }
}

export type DonationAbandonReason = 'notReadyYet' | 'wantedMoreInfo' | 'askUnclear' | 'somethingBroken' | 'dismissed';

// Ported from sportfund's TSF-283 (commit bb045a2) via mystudyfund's own port
// (commit a5e4fbf): same event name, same reason taxonomy, (reason, creatorId)
// argument order kept identical so all three products' PostHog data stays
// comparable.
export function trackDonationAbandonFeedback(reason: DonationAbandonReason, creatorId: string): void {
    if (typeof window === 'undefined') return;
    try {
        posthog.capture('donation_abandon_feedback', { reason, creatorId });
    } catch {
        /* posthog not initialised — nothing to record */
    }
}

export type CheckoutCancelReason = 'priceTooHigh' | 'paymentMethodIssue' | 'changedMyMind' | 'somethingBroken';

// Ported from sportfund's TSF-331: checkout_started -> purchase never had a
// feedback prompt, unlike donate_ask_viewed -> checkout_started above. Fires
// when a donor comes back via cancelUrl from Stripe's hosted checkout instead
// of completing payment.
export function trackCheckoutCancelFeedback(reason: CheckoutCancelReason, creatorId: string, frequencyType?: string): void {
    if (typeof window === 'undefined') return;
    try {
        posthog.capture('checkout_cancel_feedback', { reason, creatorId, frequencyType });
    } catch {
        /* posthog not initialised — nothing to record */
    }
}


